import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import JobTabsData from './JobTabs';
import JobFilePopupData from './JobFilenamePopup';
import ConfirmPopup from './ConfirmPopup';
import Pager from './Paging';
import type { GenericList } from './models/JobSharedInterfaces';
import { DownloadFile, GetQuery, PostFormDataQuery, PostQuery } from './Shared';

interface JobFiles {
    id: number;
    dateFormatted: string;
    fileName: string;
    fileType: string;
    fileTypeId: number;
    showInApp: boolean;
    addedBy: string;
}

const JobFilesData = () => {
    const [loading, setLoading] = React.useState(true);
    let navigate = useNavigate();
    const { id } = useParams();
    const [siteAddress, setSiteAddress] = React.useState("");
    const [files, setFiles] = React.useState<JobFiles[]>([]);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);
    const [showConfirmModal, setShowConfirm] = React.useState(false);
    const [modalSaveDisabled, setModalSaveDisabled] = React.useState(false);
    const [showFilenameModal, setShowFilenameModal] = React.useState(false);
    const [fileId, setFileId] = React.useState(0);
    const [fileType, setFileType] = React.useState("");
    const [pageDetails, setPageDetails] = React.useState({ totalRecords: 0, pageSize: 10 });
    const [page, setPage] = React.useState(1);
    const [fileTypes, setFileTypes] = React.useState<GenericList[]>([]);
    const [typeId, setTypeId] = React.useState(-1);
    const [showAppFiles, setShowAppFiles] = React.useState(false);
    const [showAddFiles, setShowAddFiles] = React.useState(false);
    const [importTypeId, setImportTypeId] = React.useState(-1);
    const [importFiles, setImportFiles] = React.useState<File[]>([]);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        const apiUrl = 'GetFileTypes?JobId=' + id;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {            
            setFileTypes(result.data.fileTypes);
            setSiteAddress(result.data.siteAddress);
            getFiles(1, true, -1, false);
        }
    }

    const getFiles = async (pageNumber: number, getTotal: boolean, fileTypeId: number, showInApp: boolean) => {
        const apiUrl = 'GetJobFiles?jobId=' + id + '&page=' + pageNumber + '&totalRecords=' + pageDetails.totalRecords + '&getTotalRecords=' + getTotal + '&showAppFiles=' + showInApp + '&typeId=' + fileTypeId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setFiles(result.data.jobFiles);
                if (getTotal) {
                    setPageDetails({ totalRecords: result.data.totalRecords, pageSize: result.data.pageSize });
                }
            setLoading(false);
        }
    }

    const pageChanged = (page: number) => {
        setPage(page);
        getFiles(page, false, typeId, showAppFiles);
    }

    const handleShowInAppChange = (e: any) => {
        setShowAppFiles(e.target.checked);
        getFiles(1, true, typeId, e.target.checked);
    }

    const handleTypeChange = (e: any) => {
        let fileTypeId = e.target.value * 1;
        setTypeId(fileTypeId);
        getFiles(1, true, fileTypeId, showAppFiles);
    }

    const handleImportTypeChange = (e: any) => {
        delete errors["importTypeId"];
        let fileTypeId = e.target.value * 1;
        setImportTypeId(fileTypeId);
    }

    const onCellCheckboxChange = (e: any, index: number) => {
        saveShowInApp(index, e.target.checked);
    }

    const saveShowInApp = async (index: number, showInApp: boolean) => {
        var jobFiles = [...files];
        var file = jobFiles[index];
        file.showInApp = showInApp;

        var updateShowInApp = {
            id: file.id,
            showInApp: showInApp
        };

        var jsonItem = JSON.stringify(updateShowInApp);
        try {
            const apiUrl = "UpdateJobFileShowInApp";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    setFiles(jobFiles);
                    toast.success("File has been updated");
                }
            }
        } catch (e) {
            toast.error("Error saving show in app!");
        }
    }

    const onCellChange = (e: any, index: number) => {
        saveFileType(index, e.target.value * 1);
    }

    const saveFileType = async (index: number, fileTypeId: number) => {
        var jobFiles = [...files];
        var file = jobFiles[index];
        file.fileTypeId = fileTypeId;

        var saveFileType = {
            id: file.id,
            fileTypeId: fileTypeId
        };

        try {
            var jsonItem = JSON.stringify(saveFileType);

            const apiUrl = "UpdateJobFileType";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    setFiles(jobFiles);
                    toast.success("File has been updated");
                }
            }
        } catch (e) {
            toast.error("Error saving file type!");
        }
    }

    //IMPORT
    const addNewFiles = () => {
        setShowAddFiles(true);
    }

    const hideAddNewFiles = () => {
        setShowAddFiles(false);
    }

    const handleImportFile = (e: any) => {
        delete errors["importFiles"];
        setImportFiles(e.target.files);
    }

    const validate = () => {
        let error: any = {};
        let formIsValid = true;

        if (importTypeId < 1) {
            error["importTypeId"] = "File Type is required";
            formIsValid = false;
        }

        if (importFiles.length === 0) {
            error["importFiles"] = "Select one or more files to upload!";
            formIsValid = false;
        }

        setErrors(error);
        return formIsValid;
    }

    const importJobFiles = () => {
        if (validate()) {
            saveJobFiles();
        } else {
            toast.error("Fix validation errors before saving files!");
        }
    }

    const saveJobFiles = async () => {
        const filedata = new FormData()
        var jobId = id ?? "";
        filedata.append('jobId', jobId);
        filedata.append('fileTypeId', importTypeId.toString());

        for (var i = 0; i < importFiles.length; i++) {
            filedata.append('files', importFiles[i]);
        }

        try {
            const apiUrl = "SaveJobFiles";
            var result = await PostFormDataQuery(apiUrl, filedata);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                setPage(1);
                setImportTypeId(-1);
                setImportFiles([]);
                setShowAddFiles(false);
                toast.success("Files have been uploaded");
                getFiles(1, true, typeId, showAppFiles);
            }
        } catch (e) {
            toast.error("Error saving job file!");
        }
    }

    //END IMPORT

    //UPDATE FILENAME
    const updateFilename = (e: any, itemIndex: number) => {
        e.preventDefault();

        var file = files[itemIndex];
        var filename = file.fileName;
        var dotIndex = filename.lastIndexOf('.');
        var filetype = ".pdf";  //default to pdf if no filetype
        if (dotIndex >= 0) {
            var fileNameLength = filename.length;
            filetype = filename.substring(dotIndex, fileNameLength);
        }

        setFileId(file.id);
        setFileType(filetype);
        setShowFilenameModal(true);
    }

    const hideUpdateFilename = () => {
        setFileId(0);
        setFileType("");
        setShowFilenameModal(false);
    }

    const saveUpdateFilename = () => {
        setFileId(0);
        setShowFilenameModal(false);
        setFileType("");
        getFiles(page, false, typeId, showAppFiles);
    }
    //END UPDATE FILENAME

    //DELETE
    const deleteFile = (e: any, itemIndex: number) => {
        e.preventDefault();
        setDeleteIndex(itemIndex);
        setShowConfirm(true);
        setModalSaveDisabled(false);
    }

    const hideConfirmModal = () => {
        setDeleteIndex(-1);
        setShowConfirm(false);
        setModalSaveDisabled(false);
    }

    const saveConfirmModal = () => {
        if (modalSaveDisabled) {
            return;
        }

        deleteJobFile();
    }

    const deleteJobFile = async () => {
        var jobFiles = [...files];
        var file = jobFiles[deleteIndex];

        var deletedata = {
            id: file.id
        };

        //delete photo
        try {
            var jsonItem = JSON.stringify(deletedata);

            const apiUrl = "DeleteJobFile";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    jobFiles.splice(deleteIndex, 1);
                    setFiles(jobFiles);
                    setDeleteIndex(-1);
                    setShowConfirm(false);
                    setModalSaveDisabled(true);
                    toast.success("File has been deleted");
                }
            }
        } catch (e) {
            toast.error("Error deleting job file!");
        }
    }
    //END DELETE

    //DOWNLOAD FILE
    const downloadFile = (e: any, itemIndex: number) => {
        e.preventDefault();

        var file = files[itemIndex];

        downloadJobFile(file.id);
    }

    const downloadJobFile = async (fileId: number) => {
        var filename = "jobfile.pdf";
        const apiUrl = 'GetJobFile?Id=' + fileId;
        await DownloadFile(apiUrl, filename);
    }
    //END DOWNLOAD FILE

    const confirmPopup = <ConfirmPopup heading="Delete File?" text="Are you sure you want to delete this file?" hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />
    const filenamePopup = <JobFilePopupData fileId={fileId} fileType={fileType} showModal={showFilenameModal} hideModal={hideUpdateFilename} saveModal={saveUpdateFilename} />

    const tabChanged = (url: string) => {
        navigate(url);
    }

    var pagerContents;
    if ((pageDetails.totalRecords / pageDetails.pageSize) > 1) {
        //only render if we have records
        pagerContents = <Pager page={page} pageSize={pageDetails.pageSize} totalRecords={pageDetails.totalRecords} pageChanged={pageChanged} />
    }
    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"files"} tabChanged={tabChanged} />;

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
                {filenamePopup}
            </div>
            {jobTab}
            <div className={showAddFiles ? "hidden" : ""}>
                <button className="defaultbutton defaultbutton__small form-margin" onClick={addNewFiles} type="button">Add Files</button>
            </div>
            <div className={showAddFiles ? "" : "hidden" }>
                <h3>Add New File/s</h3>
                <div>
                    <label className="input-group" htmlFor="importTypeId">
                        <span className="label">File Type </span>
                        <select className="select" id="importTypeId" name="importTypeId" value={importTypeId} onChange={(e) => handleImportTypeChange(e)}>
                            <option defaultValue="-1"></option>
                            {fileTypes.map(type =>
                                <option key={type.id} value={type.id}>{type.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["importTypeId"] ? "label errors" : "hidden"}>{errors["importTypeId"]}</span>
                </div>
                <div className="marginTop10">
                    <label className="input-group" htmlFor="file">
                        <span className="label">Select Files </span>
                        <input type="file" className="marginBottom10" name="file" accept="application/pdf" onChange={handleImportFile} multiple />
                    </label>
                    <span className={errors["importFiles"] ? "label errors" : "hidden"}>{errors["importFiles"]}</span>
                </div>
                <button className="defaultbutton defaultbutton-prev" onClick={importJobFiles} type="button">Save Files</button>
                <button className="defaultbutton defaultbutton-next" onClick={hideAddNewFiles} type="button">Cancel</button>
            </div>
            <hr></hr>
            <div className="makeitflex">
                <label className="input-group--inline" htmlFor="typeId">
                    <span className="label">Type </span>
                    <select className="select" id="typeId" name="typeId" value={typeId} onChange={(e) => handleTypeChange(e)}>
                        <option defaultValue="-1"></option>
                        {fileTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <label className="input-group--inline job__checkboxPosition" htmlFor="showAppFiles">
                    <span className="label">Show App Files ONLY </span>
                    <div>
                        <input type="checkbox" name="showAppFiles" id="showAppFiles" checked={showAppFiles} onChange={(e) => handleShowInAppChange(e)}></input>
                    </div>
                </label>
            </div>
            <div className="job__noteGridSize overflowAuto">
                <table className="table tableColours">
                    <thead>
                        <tr>
                            <th>Date/Time</th>
                            <th></th>
                            <th>Filename</th>
                            <th>File Type</th>
                            <th className="textaligncenter">Show in App</th>
                            <th>Added By</th>
                            <th></th>
                        </tr>
                    </thead>
                    {files.map((file, index) =>
                        <tbody key={file.id}>
                            <tr>
                                <td className="table__text--align">{file.dateFormatted}</td>
                                <td className="table__text--align">
                                    <a className="tooltipitem tooltipitem__right alignIconCenter" href="#" onClick={(e) => updateFilename(e, index)}>
                                        <span className="fas fa-edit copy--icon"></span>
                                        <span className="tooltipitemtext tooltipitemtext__right">Update filename</span>
                                    </a>
                                </td>
                                <td className="table__text--align">
                                    <a href="#" onClick={(e) => downloadFile(e, index)}>{file.fileName}</a>
                                </td>
                                <td className="table__text--align">
                                    <select className="select" id="fileTypeId" name="fileTypeId" value={file.fileTypeId} onChange={(e) => onCellChange(e, index)}>
                                        <option hidden defaultValue="-1"></option>
                                        {fileTypes.map(type =>
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className="table__text--align">
                                    <input type='checkbox' id="selected" name="selected" className='alignIconCenter' checked={file.showInApp} onChange={(e) => onCellCheckboxChange(e, index)} />
                                </td>
                                <td className="table__text--align">{file.addedBy}</td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteFile(e, index)}>
                                            <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                        </a>
                                    </div> 
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            {pagerContents}
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobFilesData;
